<ng-container *ngIf="patient() as activePatient; else noPatient">
<div class="consult-container">
  <header class="consult-header">
    <div class="consult-header__info">
      <div>
        <h1>{{ patientName() }}</h1>
        <p>{{ patientAge() }} • {{ activePatient.gender }} • {{ activePatient.phone }}</p>
      </div>
      <div class="consult-header__tabs">
        <button class="active">Consult</button>
        <button>Vaccination</button>
        <button>Growth</button>
        <button>Timeline</button>
        <button>Reports</button>
        <button>Attachment</button>
      </div>
    </div>
    <div class="consult-header__actions">
      <app-button variant="secondary">Templates</app-button>
      <app-button variant="secondary" (clicked)="previewPdf()">Preview Rx</app-button>
      <app-button variant="primary" (clicked)="saveConsultation()">Save</app-button>
    </div>
  </header>

  <div class="consult-body">
    <aside class="consult-sidebar">
      <section class="sidebar-card">
        <div class="sidebar-card__header">
          <span>Medical History</span>
          <button class="icon-button" title="View full history">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <div class="sidebar-card__content">
          @if (activePatient.medicalHistory?.length) {
            <ul class="history-list">
              @for (item of activePatient.medicalHistory; track item) {
                <li>{{ item }}</li>
              }
            </ul>
          } @else {
            <p class="empty-text">No past history recorded</p>
          }
        </div>
      </section>

      <section class="sidebar-card">
        <div class="sidebar-card__header"><span>Vitals</span></div>
        <div class="vitals-grid">
          <label>Temperature (°C)
            <input type="number" step="0.1" [value]="vitals().temperature ?? ''"
                   (input)="updateVital('temperature', ($any($event.target)).value)" />
          </label>
          <label>Pulse (/min)
            <input type="number" [value]="vitals().heartRate ?? ''"
                   (input)="updateVital('heartRate', ($any($event.target)).value)" />
          </label>
          <label>Systolic (mmHg)
            <input type="text" [value]="vitals().bloodPressure?.split('/')?.[0] || ''"
                   (input)="updateBloodPressure('systolic', ($any($event.target)).value)" />
          </label>
          <label>Diastolic (mmHg)
            <input type="text" [value]="vitals().bloodPressure?.split('/')?.[1] || ''"
                   (input)="updateBloodPressure('diastolic', ($any($event.target)).value)" />
          </label>
          <label>Respiration (/min)
            <input type="number" [value]="vitals().respiratoryRate ?? ''"
                   (input)="updateVital('respiratoryRate', ($any($event.target)).value)" />
          </label>
          <label>SpO₂ (%)
            <input type="number" [value]="vitals().spo2 ?? ''"
                   (input)="updateVital('spo2', ($any($event.target)).value)" />
          </label>
          <label>Weight (kg)
            <input type="number" step="0.1" [value]="vitals().weight ?? ''"
                   (input)="updateVital('weight', ($any($event.target)).value)" />
          </label>
        </div>
      </section>

      <section class="sidebar-card">
        <div class="sidebar-card__header"><span>Prescription Notes</span></div>
        <textarea class="sidebar-textarea" placeholder="This note is printed"
                  [value]="prescriptionNotes()"
                  (input)="prescriptionNotes.set(($any($event.target)).value)"></textarea>
      </section>

      <section class="sidebar-card">
        <div class="sidebar-card__header"><span>Doctor Notes</span></div>
        <textarea class="sidebar-textarea" placeholder="This note is not printed"
                  [value]="doctorNotes()"
                  (input)="doctorNotes.set(($any($event.target)).value)"></textarea>
      </section>
    </aside>

    <main class="consult-main">
      <section class="main-card">
        <div class="main-card__header">
          <div class="section-title">Chief Complaint</div>
        </div>
        <textarea class="input-field h-24" placeholder="Enter the main reason for visit..."
                  [ngModel]="chiefComplaint()"
                  (ngModelChange)="chiefComplaint.set($event)"></textarea>
      </section>

      <section class="main-card">
        <div class="main-card__header">
          <div class="section-title">Symptoms</div>
          <div class="symptom-actions">
            <button class="link-button">Past symptoms</button>
            <div class="relative">
              <input type="text" class="search-input" placeholder="Search symptoms">
            </div>
          </div>
        </div>
        <div class="symptom-chips">
          @for (symptom of availableSymptoms(); track symptom.id) {
            <button
              (click)="onSymptomSelect(symptom)"
              [disabled]="isSymptomSelected(symptom.id)"
              [class.selected]="isSymptomSelected(symptom.id)">
              {{ symptom.name }}
            </button>
          }
        </div>
        @if (selectedSymptoms().length === 0) {
          <p class="empty-text">Select symptoms to continue consultation.</p>
        } @else {
          <div class="selected-symptom-list">
            @for (symptom of selectedSymptoms(); track symptom.id) {
              <div class="selected-symptom-item">
                <div>
                  <p class="name">{{ symptom.name }}</p>
                  <p class="meta">{{ symptom.category }}</p>
                </div>
                <div class="actions">
                  <select [(ngModel)]="symptom.severity">
                    <option value="Mild">Mild</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Severe">Severe</option>
                  </select>
                  <button (click)="removeSymptom(symptom.id)">Remove</button>
                </div>
              </div>
            }
          </div>
        }
      </section>

      <section class="main-card">
        <div class="main-card__header">
          <div class="section-title">Findings</div>
        </div>
        <div class="findings-grid">
          @if (suggestedQuestions().length === 0) {
            <p class="empty-text">Add symptoms to get suggested questions.</p>
          } @else {
            @for (question of suggestedQuestions(); track question.id) {
              <div class="finding-item" (click)="openQuestion(question)">
                <div>
                  <p class="question">{{ question.question }}</p>
                  <p class="meta">{{ question.category }}</p>
                </div>
                <div class="status" [class.answered]="isQuestionAnswered(question.id)">
                  {{ isQuestionAnswered(question.id) ? 'Answered' : 'Answer' }}
                </div>
              </div>
            }
          }
        </div>
      </section>

      <section class="main-card">
        <div class="main-card__header">
          <div class="section-title">Diagnosis</div>
          <button class="link-button" (click)="addDiagnosis()">Add</button>
        </div>
        @if (diagnosis().length === 0) {
          <p class="empty-text">No diagnosis yet.</p>
        } @else {
          <div class="chip-list">
            @for (diag of diagnosis(); track $index) {
              <span class="chip">
                {{ diag }}
                <button class="remove" (click)="removeDiagnosis($index)">×</button>
              </span>
            }
          </div>
        }
      </section>

      <section class="main-card">
        <div class="main-card__header">
          <div class="section-title">Medicines</div>
        </div>
        @if (suggestedPrescriptions().length === 0) {
          <p class="empty-text">Add symptoms to get medicine suggestions.</p>
        } @else {
          <div class="medicine-grid">
            @for (prescription of suggestedPrescriptions(); track prescription.medication) {
              <div class="medicine-card" [class.selected]="isPrescriptionSelected(prescription.medication)"
                   (click)="togglePrescription(prescription)">
                <div class="medicine-header">
                  <h4>{{ prescription.medication }}</h4>
                  <span>{{ prescription.dosage }}</span>
                </div>
                <p class="meta">{{ prescription.frequency }} • {{ prescription.duration }}</p>
                <p class="description">{{ prescription.reason }}</p>
              </div>
            }
          </div>
        }
      </section>

      <section class="main-card">
        <h3 class="section-title">Investigations</h3>
        <button class="link-button" (click)="addInvestigation()">Add Investigation</button>
        @if (investigations().length === 0) {
          <p class="empty-text">No investigations added.</p>
        } @else {
          <div class="investigation-list">
            @for (investigation of investigations(); track $index) {
              <div class="investigation-item">
                <input type="text" placeholder="Investigation" [value]="investigation.name"
                       (input)="updateInvestigationField($index, 'name', ($any($event.target)).value)" />
                <select [value]="investigation.status"
                        (change)="updateInvestigationStatus($index, ($any($event.target)).value)">
                  <option value="Pending">Pending</option>
                  <option value="Ordered">Ordered</option>
                  <option value="Completed">Completed</option>
                </select>
                <input type="text" placeholder="Notes" [value]="investigation.notes || ''"
                       (input)="updateInvestigationField($index, 'notes', ($any($event.target)).value)" />
                <button (click)="removeInvestigation($index)" class="remove">Remove</button>
              </div>
            }
          </div>
        }
      </section>

      <section class="main-card">
        <div class="main-card__header">
          <div class="section-title">Family History</div>
          <button class="link-button" (click)="addFamilyHistory()">Add</button>
        </div>
        @if (familyHistory().length === 0) {
          <p class="empty-text">No family history recorded.</p>
        } @else {
          <div class="family-list">
            @for (history of familyHistory(); track $index) {
              <div class="family-item">
                <input type="text" placeholder="Condition" [value]="history.condition"
                       (input)="updateFamilyHistory($index, 'condition', ($any($event.target)).value)" />
                <input type="text" placeholder="Relation" [value]="history.relation"
                       (input)="updateFamilyHistory($index, 'relation', ($any($event.target)).value)" />
                <input type="text" placeholder="Notes" [value]="history.notes || ''"
                       (input)="updateFamilyHistory($index, 'notes', ($any($event.target)).value)" />
                <button class="remove" (click)="removeFamilyHistory($index)">Remove</button>
              </div>
            }
          </div>
        }
      </section>

      <section class="main-card">
        <h3 class="section-title">Advice Self Monitoring</h3>
        <p class="advice-text">Instruct your patients to log parameters at home as per your advice. Data syncs in real time on EMR.</p>
        <div class="advice-grid">
          <div>
            <h4>Patient logs vitals</h4>
            <p>Record data at home effortlessly.</p>
          </div>
          <div>
            <h4>Real-time sync</h4>
            <p>Monitor trends and intervene early.</p>
          </div>
          <div>
            <h4>Easy access</h4>
            <p>Review historical values in one place.</p>
          </div>
        </div>
      </section>

      <section class="main-card">
        <h3 class="section-title">Follow-up Plan</h3>
        <label class="toggle">
          <input type="checkbox" [checked]="followUpEnabled()" (change)="followUpEnabled.set(($any($event.target)).checked)" />
          <span>Enable automatic follow-up scheduling</span>
        </label>
        @if (followUpEnabled()) {
          <div class="followup-grid">
            <label>Date
              <input type="date" [value]="followUpDate() | date:'yyyy-MM-dd'" (input)="updateFollowUpDate(($any($event.target)).value)" />
            </label>
            <label>Time
              <input type="time" [value]="followUpTime()" (input)="updateFollowUpTime(($any($event.target)).value)" />
            </label>
            <label>Notes
              <input type="text" [value]="followUpNotes()" (input)="updateFollowUpNotes(($any($event.target)).value)" placeholder="e.g., Review labs" />
            </label>
          </div>
        }
      </section>
    </main>
  </div>

  @if (showQuestionModal() && currentQuestion()) {
    <div class="modal-backdrop" (click)="showQuestionModal.set(false)">
      <div class="modal" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3>{{ currentQuestion()!.question }}</h3>
          <p>{{ currentQuestion()!.category }}</p>
        </div>
        <div class="modal-body">
          @if (currentQuestion()!.type === 'yesno') {
            <div class="modal-options">
              <button (click)="currentAnswer.set(true)" [class.selected]="currentAnswer() === true">Yes</button>
              <button (click)="currentAnswer.set(false)" [class.selected]="currentAnswer() === false">No</button>
            </div>
          } @else if (currentQuestion()!.type === 'text') {
            <textarea class="modal-textarea" [ngModel]="currentAnswer()" (ngModelChange)="currentAnswer.set($event)"></textarea>
          } @else if (currentQuestion()!.type === 'scale') {
            <div class="modal-scale">
              <input type="range" min="1" max="10" [ngModel]="currentAnswer()" (ngModelChange)="currentAnswer.set($event)" />
              <span>Rating: {{ currentAnswer() || 1 }}/10</span>
            </div>
          }
        </div>
        <div class="modal-footer">
          <app-button variant="secondary" (clicked)="showQuestionModal.set(false)">Cancel</app-button>
          <app-button variant="primary" (clicked)="saveQuestionAnswer()">Save</app-button>
        </div>
      </div>
    </div>
  }
</div>
</ng-container>

<ng-template #noPatient>
  <div class="consult-container">
    <div class="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-500">
      Select a patient to begin consultation.
    </div>
  </div>
</ng-template>

